import {
  AnyRendererProps,
  FieldType,
  RendererWithValue,
  RenderOption,
} from './types';
import {
  APIComponentAttributes,
  APIEntityType,
  APIFieldType,
  ArdoqId,
  ArrowType,
  DateRangeFieldDefaultValue,
  isContentTypeSupportedByImagePreview,
  type Maybe,
  type Provenance,
} from '@ardoq/api-types';
import { readRawValue } from './utils/readRawValue';
import { getNumberFormatOptions } from './utils/getNumberFormatOptions';
import { getReferenceTypeByReferenceId } from './utils/referenceType';
import { getComponentTypeByComponentId } from './utils/componentType';
import { getFieldType } from './utils/fields';
import { logError } from '@ardoq/logging';
import { getIcon, ReferenceLinePreview } from '@ardoq/icons';
import { DEFAULT_MAX_TEXT_LENGTH, MarkdownInPopover } from './atoms';
import { TextOverflow, TruncatedTextPopover } from '@ardoq/popovers';
import { getArrowTypeLabel, getLineTypeLabel } from '../utils';
import {
  EntityIcon,
  representationDataToEntityIconProps,
} from '@ardoq/basic-components';
import { EntitiesEllipsis } from './EntitiesEllipsis';
import {
  formatNumber,
  getCurrentLocale,
  numberFormatOptions,
} from '@ardoq/locale';
import { ComponentIconRepresentation } from '../DiffMergeTable/EntityRepresentation/ComponentIconRepresentation';
import { MarkdownViewer } from '@ardoq/markdown';
import {
  BooleanDecorator,
  ColorDecorator,
  DateDecorator,
  DecoratorPlaceholder,
  DecoratorWrapper,
  EntityDecorator,
  LinkDecorator,
  UserDecorator,
} from '@ardoq/decorators';
import { ComponentLinkHandler } from '../DiffMergeTable/EntityRepresentation/ComponentRepresentationWithLink';
import {
  getHasOnlyWhiteCharacters,
  isShiftXUrl,
  urlUtils,
} from '@ardoq/common-helpers';
import {
  getCheckedValueLabel,
  getUncheckedValueLabel,
  getUnsetValueLabel,
} from './consts';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { DropdownItem } from '@ardoq/dropdown-menu';
import { keyBy } from 'lodash';
import { showImagePreviewModal } from '@ardoq/image-preview';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { colors } from '@ardoq/design-tokens';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { ProvenanceValue } from '@ardoq/provenance';

export const NoValue = ({
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: {
  i18nLocale?: I18nLocale;
} = {}) => (
  <DecoratorPlaceholder>{getUnsetValueLabel(i18nLocale)}</DecoratorPlaceholder>
);

export const Nonexistent = ({
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: {
  i18nLocale?: I18nLocale;
} = {}) => (
  <DecoratorPlaceholder>
    {t(i18nLocale, 'renderers:nonexistent', 'N/A')}
  </DecoratorPlaceholder>
);

export const Empty = ({
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: {
  i18nLocale?: I18nLocale;
} = {}) => (
  <DecoratorPlaceholder>
    {t(i18nLocale, 'renderers:empty', 'Empty')}
  </DecoratorPlaceholder>
);

const UserBase = ({
  userId,
  users,
  underlineOnHover,
  variant,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: { userId?: string } & Pick<
  AnyRendererProps,
  'users' | 'underlineOnHover' | 'variant' | 'i18nLocale'
>) => {
  if (!userId) return <NoValue i18nLocale={i18nLocale} />;
  const { name, email, image } = users[userId] || {};
  if (name || email) {
    return (
      <UserDecorator
        name={name}
        email={email}
        image={image}
        underlineOnHover={underlineOnHover}
        color={variant === 'light' ? colors.textModerate : undefined}
      />
    );
  }
  return (
    <DecoratorPlaceholder>
      {t(i18nLocale, 'renderers:noUserFound', 'No user found')}
    </DecoratorPlaceholder>
  );
};

export const User = ({
  users,
  value: userId,
  underlineOnHover,
  variant,
  i18nLocale,
}: RendererWithValue) => (
  <UserBase
    userId={userId}
    users={users}
    underlineOnHover={underlineOnHover}
    variant={variant}
    i18nLocale={i18nLocale}
  />
);

export const Parent = ({
  enhancedScopeData,
  value,
  ...props
}: RendererWithValue) =>
  value ? (
    <Component
      {...props}
      entityId={value}
      enhancedScopeData={enhancedScopeData}
    />
  ) : (
    <NoValue i18nLocale={props.i18nLocale} />
  );

export const Component = ({
  entityId,
  enhancedScopeData,
  parentEntityId,
  graphics,
}: Pick<
  RendererWithValue,
  'entityId' | 'enhancedScopeData' | 'parentEntityId' | 'graphics'
>) => {
  const name = readRawValue(
    APIEntityType.COMPONENT,
    entityId,
    'name',
    enhancedScopeData,
    parentEntityId
  );

  return (
    <DecoratorWrapper>
      {enhancedScopeData && (
        <ComponentIconRepresentation
          entityType={APIEntityType.COMPONENT}
          entityId={entityId as string}
          enhancedScopeData={enhancedScopeData}
          graphics={graphics}
        />
      )}
      <TextOverflow renderIn="tooltip">{name}</TextOverflow>
    </DecoratorWrapper>
  );
};

export const RootWorkspace = (props: RendererWithValue) => {
  const { value: workspaceId } = props;
  if (workspaceId) {
    return <Workspace {...props} entityId={String(workspaceId)} />;
  }
  return <NoValue i18nLocale={props.i18nLocale} />;
};

const Workspace = ({
  entityId,
  enhancedScopeData,
  parentEntityId,
}: AnyRendererProps) => {
  const name = readRawValue(
    APIEntityType.WORKSPACE,
    entityId,
    'name',
    enhancedScopeData,
    parentEntityId
  );

  return <EntityDecorator label={name} />;
};

export const WorkspaceModel = ({
  entityType,
  entityId,
  enhancedScopeData,
  parentEntityId,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: AnyRendererProps) => {
  const modelId = readRawValue(
    entityType,
    entityId,
    'model',
    enhancedScopeData,
    parentEntityId
  );
  const workspace = enhancedScopeData.workspaces.find(
    ({ componentModel }) => componentModel === modelId
  );

  if (!workspace) {
    logError(Error(`Model without owning workspace: ${entityId}`));
    return <EntityDecorator label={`${entityId}`} />;
  }

  const modelSuffix = t(i18nLocale, 'renderers:modelSuffix', '(Model)');
  return (
    <TruncatedTextPopover>{`${workspace.name} ${modelSuffix}`}</TruncatedTextPopover>
  );
};

export const EntityType = ({
  type,
  i18nLocale,
}: {
  type: { name: string } | null;
  i18nLocale?: I18nLocale;
}) => {
  if (type) {
    return <EntityDecorator label={type.name} />;
  }
  return <Nonexistent i18nLocale={i18nLocale} />;
};

export const ComponentType = ({
  entityId,
  enhancedScopeData,
  i18nLocale,
}: AnyRendererProps) => (
  <EntityType
    type={getComponentTypeByComponentId(entityId as string, enhancedScopeData)}
    i18nLocale={i18nLocale}
  />
);

export const ReferenceType = ({
  entityId,
  enhancedScopeData,
  i18nLocale,
}: {
  entityId: AnyRendererProps['entityId'];
  enhancedScopeData: AnyRendererProps['enhancedScopeData'];
  i18nLocale?: I18nLocale;
}) => (
  <EntityType
    type={getReferenceTypeByReferenceId(entityId as string, enhancedScopeData)}
    i18nLocale={i18nLocale}
  />
);

const GenericList = ({
  list,
  i18nLocale,
}: {
  list: string[];
  i18nLocale?: I18nLocale;
}) => {
  if (!list.length) return <NoValue i18nLocale={i18nLocale} />;
  return <TruncatedTextPopover>{list.join(', ')}</TruncatedTextPopover>;
};

const ApplyToAll = ({
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: {
  i18nLocale?: I18nLocale;
} = {}) => (
  <TruncatedTextPopover>
    {t(i18nLocale, 'renderers:applyToAll', 'Apply to all')}
  </TruncatedTextPopover>
);

export const EntitiesList = ({
  entityIds,
  entityType,
  parentEntityId,
  enhancedScopeData,
  graphics,
  options,
  popoverWidth,
  hideDefaultPopover,
  getComponentLinkHandler,
  getComponentNavigationOptions,
  underlineOnHover,
  variant,
  i18nLocale,
}: Pick<
  AnyRendererProps,
  | 'options'
  | 'enhancedScopeData'
  | 'entityType'
  | 'parentEntityId'
  | 'graphics'
  | 'underlineOnHover'
  | 'i18nLocale'
> & {
  entityIds: ArdoqId[];
  popoverWidth?: string;
  hideDefaultPopover?: boolean;
  getComponentLinkHandler?: (
    componentId: ArdoqId
  ) => ComponentLinkHandler | undefined;
  getComponentNavigationOptions?: (
    component: APIComponentAttributes
  ) => DropdownItem[];
  variant?: 'default' | 'light';
}) => {
  if (!entityIds.length) return <Empty i18nLocale={i18nLocale} />;
  return (
    <EntitiesEllipsis
      maxLength={options.definitions[RenderOption.TEXT_ELLIPSIS]?.maxLength}
      entityIds={entityIds}
      entityType={entityType}
      parentEntityId={parentEntityId}
      enhancedScopeData={enhancedScopeData}
      graphics={graphics}
      popoverWidth={popoverWidth}
      hideDefaultPopover={hideDefaultPopover}
      getComponentLinkHandler={getComponentLinkHandler}
      getComponentNavigationOptions={getComponentNavigationOptions}
      underlineOnHover={underlineOnHover}
      variant={variant}
    />
  );
};

const getModelIdByFieldId = (
  fieldId: ArdoqId,
  enhancedScopeData: EnhancedScopeData
) => enhancedScopeData.fieldsById[fieldId]?.model;

export const ComponentTypeList = ({
  entityId,
  enhancedScopeData,
  graphics,
  options,
  i18nLocale,
}: AnyRendererProps) => {
  const { componentType: componentTypeIds, global } =
    enhancedScopeData.fieldsById[entityId];

  return global ? (
    <ApplyToAll i18nLocale={i18nLocale} />
  ) : (
    <EntitiesList
      entityIds={componentTypeIds}
      enhancedScopeData={enhancedScopeData}
      entityType={APIEntityType.COMPONENT_TYPE}
      parentEntityId={getModelIdByFieldId(
        entityId as string,
        enhancedScopeData
      )}
      graphics={graphics}
      options={options}
      i18nLocale={i18nLocale}
    />
  );
};

export const ReferenceTypeList = ({
  entityId,
  enhancedScopeData,
  options,
  graphics,
  i18nLocale,
}: AnyRendererProps) => {
  const { referenceType: referenceTypeIds, globalref } =
    enhancedScopeData.fieldsById[entityId];
  return globalref ? (
    <ApplyToAll i18nLocale={i18nLocale} />
  ) : (
    <EntitiesList
      entityIds={referenceTypeIds}
      enhancedScopeData={enhancedScopeData}
      entityType={APIEntityType.REFERENCE_TYPE}
      parentEntityId={getModelIdByFieldId(
        entityId as string,
        enhancedScopeData
      )}
      graphics={graphics}
      options={options}
      i18nLocale={i18nLocale}
    />
  );
};

export const TaggedEntityList = ({
  enhancedScopeData,
  graphics,
  fieldName,
  options,
  value: entityIds,
  i18nLocale,
}: RendererWithValue) => (
  <EntitiesList
    entityIds={entityIds}
    entityType={
      fieldName === 'components'
        ? APIEntityType.COMPONENT
        : APIEntityType.REFERENCE
    }
    enhancedScopeData={enhancedScopeData}
    graphics={graphics}
    popoverWidth={fieldName === 'references' ? '800px' : 'auto'}
    options={options}
    i18nLocale={i18nLocale}
  />
);

export const DefaultValueAttribute = ({
  enhancedScopeData,
  entityId,
  users,
  i18nLocale,
}: AnyRendererProps) => {
  const { type: fieldType, defaultValue: value } =
    enhancedScopeData.fieldsById[entityId];

  switch (fieldType) {
    case APIFieldType.USER:
      return (
        <UserBase
          userId={String(value)}
          users={users}
          i18nLocale={i18nLocale}
        />
      );
    case APIFieldType.DATE_ONLY:
    case APIFieldType.DATE_TIME:
      return <DateBase date={value as string} i18nLocale={i18nLocale} />;
    case APIFieldType.DATE_ONLY_RANGE:
    case APIFieldType.DATE_TIME_RANGE:
      return (
        <DateDecorator
          dateFrom={(value as DateRangeFieldDefaultValue).start ?? ''}
          dateUntil={(value as DateRangeFieldDefaultValue).end ?? ''}
        />
      );
    case APIFieldType.LIST:
    case APIFieldType.SELECT_MULTIPLE_LIST:
      return (
        <GenericList
          list={typeof value === 'string' ? value.split(',') : []}
          i18nLocale={i18nLocale}
        />
      );
    case APIFieldType.CHECKBOX:
      return <Checkbox value={value} i18nLocale={i18nLocale} />;
    case APIFieldType.TEXT:
    case APIFieldType.TEXT_AREA:
      return typeof value === 'string' ? (
        <TruncatedTextPopover key={value}>{value}</TruncatedTextPopover>
      ) : (
        <NoValue i18nLocale={i18nLocale} />
      );
    case APIFieldType.NUMBER:
      return (
        <Number
          entityType={APIEntityType.FIELD}
          entityId={entityId}
          fieldName="defaultValue"
          enhancedScopeData={enhancedScopeData}
          value={value}
          i18nLocale={i18nLocale}
        />
      );
    default:
      return <DecoratorWrapper>{value as string}</DecoratorWrapper>;
  }
};

const PREVIEW_WIDTH = 70;

const isNone = (value: unknown): boolean => value === 'none' || value === null;

export const ReferenceTypeLine = ({
  enhancedScopeData,
  entityId,
  parentEntityId,
  fieldName: renderContext,
  i18nLocale,
}: Pick<
  AnyRendererProps,
  | 'enhancedScopeData'
  | 'entityId'
  | 'parentEntityId'
  | 'fieldName'
  | 'i18nLocale'
>) => {
  const [line, color, lineBeginning = 'none', lineEnding = 'none'] = [
    'line',
    'color',
    'lineBeginning',
    'lineEnding',
  ].map(fieldName =>
    readRawValue(
      APIEntityType.REFERENCE_TYPE,
      entityId,
      fieldName,
      enhancedScopeData,
      parentEntityId
    )
  );

  if (renderContext === 'lineBeginning' && isNone(lineBeginning)) {
    return <NoValue i18nLocale={i18nLocale} />;
  }

  if (renderContext === 'lineEnding' && isNone(lineEnding)) {
    return <NoValue i18nLocale={i18nLocale} />;
  }

  return (
    <DecoratorWrapper>
      <ReferenceLinePreview
        width={PREVIEW_WIDTH}
        line={line}
        color={color}
        lineBeginning={renderContext === 'lineBeginning' && lineBeginning}
        lineEnding={renderContext === 'lineEnding' && lineEnding}
      />
      {['lineBeginning', 'lineEnding', 'line'].includes(renderContext) && (
        <>
          {renderContext === 'lineEnding' &&
            getArrowTypeLabel(lineEnding, i18nLocale)}
          {renderContext === 'lineBeginning' &&
            getArrowTypeLabel(lineBeginning, i18nLocale)}
          {renderContext === 'line' && getLineTypeLabel(line, i18nLocale)}
        </>
      )}
    </DecoratorWrapper>
  );
};

export const ComponentTypeChildren = ({
  entityType,
  enhancedScopeData,
  parentEntityId,
  value: children,
  i18nLocale,
}: RendererWithValue) => {
  if (!children) {
    return <NoValue i18nLocale={i18nLocale} />;
  }
  const names = Object.keys(children).map(childTypeId =>
    readRawValue(
      entityType,
      childTypeId,
      'name',
      enhancedScopeData,
      parentEntityId
    )
  );

  return <GenericList list={names} i18nLocale={i18nLocale} />;
};

const DateBase = ({
  date,
  i18nLocale,
}: {
  date?: string | null | boolean;
  i18nLocale?: I18nLocale;
}) =>
  typeof date === 'string' ? (
    <DateDecorator date={date} />
  ) : (
    <NoValue i18nLocale={i18nLocale} />
  );

export const Date = ({
  value: date,
  i18nLocale,
}: {
  value: string;
  i18nLocale?: I18nLocale;
}) => <DateBase date={date ?? ''} i18nLocale={i18nLocale} />;

export const ProvenanceRenderer = ({
  value,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: {
  value: Maybe<Provenance>;
  i18nLocale?: I18nLocale;
}) => (
  <ProvenanceValue
    provenance={value}
    i18nLocale={i18nLocale}
    hasMissingRecordExplanation
  />
);

export const DateRange = ({
  value,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: {
  value?: { start: string | null; end: string | null } | null;
  i18nLocale?: I18nLocale;
}) => {
  return (
    <DateDecorator
      dateFrom={value?.start ?? ''}
      dateUntil={value?.end ?? ''}
      placeholder={t(i18nLocale, 'renderers:noValue', 'No value')}
    />
  );
};

export const Icon = ({
  entityType,
  entityId,
  enhancedScopeData,
  parentEntityId,
  value: icon,
  i18nLocale,
}: RendererWithValue) => {
  const color = enhancedScopeDataOperations.getColor(
    enhancedScopeData,
    entityType,
    entityId,
    parentEntityId
  );
  return icon ? (
    <EntityDecorator
      {...representationDataToEntityIconProps({
        style: { color },
        isImage: false,
        value: null,
        icon: getIcon(icon),
      })}
      label={icon}
    />
  ) : (
    <NoValue i18nLocale={i18nLocale} />
  );
};

export const Url = ({
  value: url,
  options,
  underlineOnHover,
  i18nLocale,
}: RendererWithValue) => {
  if (!url) return <NoValue i18nLocale={i18nLocale} />;

  const maxLength =
    options.types[FieldType.URL]?.has(RenderOption.TEXT_ELLIPSIS) &&
    options.definitions[RenderOption.TEXT_ELLIPSIS]
      ? options.definitions[RenderOption.TEXT_ELLIPSIS].maxLength
      : DEFAULT_MAX_TEXT_LENGTH;

  return (
    <LinkDecorator
      maxWidth={maxLength}
      underlineOnHover={underlineOnHover}
      href={urlUtils.maybeRelativeUrl(url, isShiftXUrl(url))}
      target="_blank"
      rel="noopener noreferrer"
    >
      {url}
    </LinkDecorator>
  );
};

export const Image = ({
  enhancedScopeData,
  value: imageUri,
  i18nLocale,
}: RendererWithValue) => {
  if (!imageUri) return <NoValue i18nLocale={i18nLocale} />;

  const imageIdRegex = /\/([^/]*?)(\?|$)/;
  const imageId = imageUri.match(imageIdRegex)?.[1] ?? null;

  const fileName =
    enhancedScopeDataOperations.getFileName(enhancedScopeData, imageId) ||
    imageId;

  return (
    <EntityDecorator
      {...representationDataToEntityIconProps({
        isImage: true,
        value: String(imageUri),
      })}
      label={decodeURI(fileName)}
    />
  );
};

export const Shape = ({
  entityType,
  entityId,
  enhancedScopeData,
  parentEntityId,
  value: shapeName,
  i18nLocale,
}: RendererWithValue) => {
  const color = enhancedScopeDataOperations.getColor(
    enhancedScopeData,
    entityType,
    entityId,
    parentEntityId
  );

  return shapeName ? (
    <DecoratorWrapper>
      <EntityIcon shapeId={String(shapeName)} color={color} />
      {shapeName}
    </DecoratorWrapper>
  ) : (
    <NoValue i18nLocale={i18nLocale} />
  );
};

export const Checkbox = ({
  value,
  i18nLocale,
}: Pick<RendererWithValue, 'value' | 'i18nLocale'>) => (
  <BooleanDecorator
    checked={value}
    trueValueLabel={getCheckedValueLabel(i18nLocale)}
    falseValueLabel={getUncheckedValueLabel(i18nLocale)}
    unsetValueLabel={getUnsetValueLabel(i18nLocale)}
  />
);

// for backward compatibility when default reference
// colors were defined as color names
const DEFAULT_REFERENCE_COLORS: { [colorName: string]: string } = {
  blue: '#00f',
  black: '#000',
  red: '#f00',
  orange: '#ffa500',
};

export const Color = ({ value, i18nLocale }: RendererWithValue) => {
  const color = DEFAULT_REFERENCE_COLORS[value] ?? value;
  return color ? (
    <ColorDecorator color={color} />
  ) : (
    <NoValue i18nLocale={i18nLocale} />
  );
};

export const TextWithOptions = (props: RendererWithValue) => {
  const {
    entityType,
    entityId,
    fieldName,
    enhancedScopeData,
    options,
    value,
    i18nLocale,
  } = props;

  const fieldType = getFieldType(
    entityType,
    entityId,
    fieldName,
    enhancedScopeData
  );

  const markdownOption =
    fieldType && options.types[fieldType]?.has(RenderOption.MARKDOWN)
      ? options.definitions[RenderOption.MARKDOWN]
      : null;

  if (!value || getHasOnlyWhiteCharacters(value))
    return <NoValue i18nLocale={i18nLocale} />;
  if (markdownOption?.renderMarkdownInline) {
    return <MarkdownViewer content={value} sanitationLevel="permissive" />;
  } else if (fieldType === FieldType.TEXT_AREA) {
    return (
      <MarkdownInPopover
        value={value}
        i18nLocale={i18nLocale ?? DEFAULT_ARDOQ_I18NLOCALE}
      />
    );
  }
  const stringedValue = String(value);
  return (
    <TruncatedTextPopover key={stringedValue}>
      {stringedValue}
    </TruncatedTextPopover>
  );
};

export const Description = ({
  value,
  variant,
  options,
  i18nLocale,
}: RendererWithValue) => {
  if (!value) return <NoValue i18nLocale={i18nLocale} />;
  if (
    options.types[FieldType.DESCRIPTION]?.has(RenderOption.MARKDOWN) &&
    options.definitions[RenderOption.MARKDOWN]?.renderMarkdownInline
  ) {
    return <MarkdownViewer content={value} sanitationLevel="permissive" />;
  }
  return (
    <MarkdownInPopover
      value={value}
      variant={variant}
      i18nLocale={i18nLocale ?? DEFAULT_ARDOQ_I18NLOCALE}
    />
  );
};

export const MultiSelect = (props: RendererWithValue) => {
  const { entityId, value, i18nLocale } = props;

  if (!value) {
    return <NoValue i18nLocale={i18nLocale} />;
  } else if (!Array.isArray(value)) {
    logError(Error('Multiselect value not an array'), null, { entityId });
    return <Any {...props} />;
  } else if (!value.length) {
    return <NoValue i18nLocale={i18nLocale} />;
  }

  const text = value.join(', ');
  return <TruncatedTextPopover>{text}</TruncatedTextPopover>;
};

export const Any = ({ value, i18nLocale }: RendererWithValue) => {
  if (value === undefined || value === null || typeof value === 'object') {
    return <NoValue i18nLocale={i18nLocale} />;
  }

  if (typeof value === 'string') {
    return <TruncatedTextPopover>{value}</TruncatedTextPopover>;
  }

  return <DecoratorWrapper>{value}</DecoratorWrapper>;
};

export const Number = ({
  entityType,
  entityId,
  fieldName,
  enhancedScopeData,
  value,
  i18nLocale,
}: Pick<
  RendererWithValue,
  | 'enhancedScopeData'
  | 'entityId'
  | 'entityType'
  | 'fieldName'
  | 'value'
  | 'i18nLocale'
>) => {
  const formatOptions = getNumberFormatOptions(
    entityId as string,
    entityType,
    enhancedScopeData,
    fieldName
  );
  return value === undefined || value === null ? (
    <NoValue i18nLocale={i18nLocale} />
  ) : (
    <EntityDecorator
      label={formatNumber(
        value,
        formatOptions ?? undefined,
        getCurrentLocale()
      )}
    />
  );
};

export const NumberFormatOptionsRender = ({
  entityType,
  entityId,
  fieldName,
  enhancedScopeData,
  i18nLocale,
}: Pick<
  AnyRendererProps,
  'enhancedScopeData' | 'entityId' | 'entityType' | 'fieldName' | 'i18nLocale'
>) => {
  const formatOption = getNumberFormatOptions(
    entityId as string,
    entityType,
    enhancedScopeData,
    fieldName
  );
  const label = formatOption && numberFormatOptions.get(formatOption)?.label;
  return label ? (
    <DecoratorWrapper>{label}</DecoratorWrapper>
  ) : (
    <NoValue i18nLocale={i18nLocale} />
  );
};

export const Cardinality = ({
  value,
  i18nLocale,
}: {
  value: ArrowType | null;
  i18nLocale?: I18nLocale;
}) => {
  const label = value ? getArrowTypeLabel(value, i18nLocale) : '';
  return label ? (
    <TruncatedTextPopover>{label}</TruncatedTextPopover>
  ) : (
    <NoValue i18nLocale={i18nLocale} />
  );
};

export const FileRenderer = ({
  enhancedScopeData,
  value: fileId,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: RendererWithValue) => {
  const attachmentsById = keyBy(enhancedScopeData.attachments ?? [], '_id');
  const file = attachmentsById[fileId];

  if (!file) {
    return <TruncatedTextPopover>{fileId}</TruncatedTextPopover>;
  }

  // files access is disabled in public slides for anonymous users, file.uri is set to '' before rendering
  if (!file.uri) {
    return <TruncatedTextPopover>{file.filename}</TruncatedTextPopover>;
  }

  const isSupportedByImagePreview = isContentTypeSupportedByImagePreview(file);

  if (!isSupportedByImagePreview) {
    return (
      <LinkDecorator
        hideIcon
        underlineOnHover
        href={file.uri}
        target="_blank"
        rel="noopener noreferrer"
      >
        {file.filename}
      </LinkDecorator>
    );
  }

  return (
    <LinkDecorator
      hideIcon
      underlineOnHover
      onClick={() => {
        showImagePreviewModal({
          title: file.filename,
          url: file.uri,
          link: {
            href: file.uri,
            label: t(i18nLocale, 'renderers:download', 'Download'),
          },
        });
      }}
    >
      {file.filename}
    </LinkDecorator>
  );
};
